import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AtomField from "../common/AtomField";
import { API_BASE as API } from "../../api/api";
import "./Auth.css";
import Logo from "../shared/Logo";
import PasswordField from "../shared/PasswordField";

const Login = () => {
  const { login }  = useAuth();
  const navigate   = useNavigate();
  const [params]   = useSearchParams();
  const verified   = params.get("verified") === "1";
  const resetDone  = params.get("reset")    === "1";
  const redirectParam = params.get("redirect");
  const redirectTo = redirectParam && redirectParam.startsWith("/") ? redirectParam : "/";

  // ── Login state ──────────────────────────────────────────────
  const [email,     setEmail]     = useState("");
  const [password,  setPassword]  = useState("");
  const [error,     setError]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [unverified,setUnverified]= useState(false);

  // ── Forgot-password state ────────────────────────────────────
  const [view,        setView]        = useState("login"); // login | reset-request | reset-confirm
  const [resetEmail,  setResetEmail]  = useState("");
  const [resetCode,   setResetCode]   = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPwd,  setConfirmPwd]  = useState("");
  const [resetError,  setResetError]  = useState("");
  const [resetLoading,setResetLoading]= useState(false);

  // ── Login handlers ───────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverified(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate(redirectTo);
    } catch (err) {
      if (err.message === "email_not_verified") {
        setUnverified(true);
      } else {
        setError(err.message || "Sign in failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const goVerify = () =>
    navigate(`/verify?email=${encodeURIComponent(email)}&type=email`);

  // ── Forgot-password handlers ─────────────────────────────────
  const handleResetRequest = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetLoading(true);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      setView("reset-confirm");
    } catch {
      setResetError("Something went wrong. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    setResetError("");
    if (newPassword.length < 8) {
      setResetError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPwd) {
      setResetError("Passwords do not match.");
      return;
    }
    setResetLoading(true);
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail, code: resetCode, new_password: newPassword }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Reset failed.");
      navigate("/login?reset=1");
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const goBackToLogin = () => {
    setView("login");
    setResetError("");
    setResetCode("");
    setNewPassword("");
    setConfirmPwd("");
  };

  // ── View: request reset code ─────────────────────────────────
  if (view === "reset-request") {
    return (
      <main className="auth-page">
        <AtomField />
        <section className="auth-card auth-card-anim">
          <div className="auth-brand-icon">
            <Logo className="auth-logo" />
          </div>

          <h1 className="auth-anim-title">Reset Password</h1>
          <p className="auth-anim-sub">
            Enter your account email and we'll send a reset code to your inbox.
          </p>

          {resetError && (
            <div className="auth-error auth-anim-field" style={{ "--i": 0 }}>
              {resetError}
            </div>
          )}

          <form onSubmit={handleResetRequest} className="auth-form">
            <label className="form-field auth-anim-field" style={{ "--i": 1 }}>
              Email
              <input
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                type="email" required className="form-input" autoComplete="username"
                placeholder="you@example.com"
              />
            </label>
            <button
              className="form-button auth-anim-field" style={{ "--i": 2 }}
              type="submit" disabled={resetLoading}
            >
              {resetLoading
                ? <span className="auth-btn-loading"><span/><span/><span/></span>
                : "Send Reset Code"}
            </button>
          </form>

          <p className="auth-footer-link auth-anim-field" style={{ "--i": 3 }}>
            <button className="forgot-back-btn" onClick={goBackToLogin}>
              ← Back to Sign In
            </button>
          </p>
        </section>
      </main>
    );
  }

  // ── View: enter code + new password ─────────────────────────
  if (view === "reset-confirm") {
    return (
      <main className="auth-page">
        <AtomField />
        <section className="auth-card auth-card-anim">
          <div className="auth-brand-icon">
            <Logo className="auth-logo" />
          </div>

          <h1 className="auth-anim-title">Set New Password</h1>
          <p className="auth-anim-sub">
            A 6-digit code was sent to your inbox for{" "}
            <span className="reset-email-chip">{resetEmail}</span>.
          </p>

          {resetError && (
            <div className="auth-error auth-anim-field" style={{ "--i": 0 }}>
              {resetError}
            </div>
          )}

          <form onSubmit={handleResetConfirm} className="auth-form">
            <label className="form-field auth-anim-field" style={{ "--i": 1 }}>
              Reset Code
              <input
                value={resetCode}
                onChange={e => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                type="text" inputMode="numeric" required
                className="form-input reset-code-input"
                placeholder="6-digit code"
                maxLength={6}
              />
            </label>
            <PasswordField
              label="New Password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Minimum 8 characters"
              className="auth-anim-field" style={{ "--i": 2 }}
            />
            <PasswordField
              label="Confirm Password"
              value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              autoComplete="new-password"
              className="auth-anim-field" style={{ "--i": 3 }}
            />
            <button
              className="form-button auth-anim-field" style={{ "--i": 4 }}
              type="submit" disabled={resetLoading}
            >
              {resetLoading
                ? <span className="auth-btn-loading"><span/><span/><span/></span>
                : "Reset Password"}
            </button>
          </form>

          <p className="auth-footer-link auth-anim-field" style={{ "--i": 5 }}>
            <button className="forgot-back-btn" onClick={() => setView("reset-request")}>
              ← Try a different email
            </button>
          </p>
        </section>
      </main>
    );
  }

  // ── View: normal login ───────────────────────────────────────
  return (
    <main className="auth-page">
      <AtomField />

      <section className="auth-card auth-card-anim">
        <div className="auth-brand-icon">
          <Logo className="auth-logo" />
        </div>

        <h1 className="auth-anim-title">Welcome Back</h1>
        <p className="auth-anim-sub">Sign in to your Emporia account.</p>

        {verified && (
          <div className="auth-help auth-anim-field" style={{ "--i": 0 }}>
            ✓ Email verified — you can now sign in.
          </div>
        )}

        {resetDone && (
          <div className="auth-help auth-anim-field" style={{ "--i": 0 }}>
            ✓ Password reset — sign in with your new password.
          </div>
        )}

        {error && (
          <div className="auth-error auth-anim-field" style={{ "--i": 0 }}>
            {error}
          </div>
        )}

        {unverified && (
          <div className="unverified-banner auth-anim-field" style={{ "--i": 0 }}>
            <p className="unverified-title">Email not verified</p>
            <p className="unverified-body">
              Please verify <strong>{email}</strong> before signing in.
              Check your inbox for a 6-digit code.
            </p>
            <div className="unverified-actions">
              <button className="unverified-btn-primary" onClick={goVerify}>
                Enter verification code
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <label className="form-field auth-anim-field" style={{ "--i": 1 }}>
            Email
            <input
              value={email}
              onChange={e => { setEmail(e.target.value); setUnverified(false); }}
              type="email" required className="form-input" autoComplete="username"
              placeholder="you@example.com"
            />
          </label>
          <PasswordField
            label="Password"
            value={password} onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            className="auth-anim-field" style={{ "--i": 2 }}
          />

          <div className="forgot-link-row auth-anim-field" style={{ "--i": 2 }}>
            <button
              type="button"
              className="forgot-password-link"
              onClick={() => { setResetEmail(email); setView("reset-request"); }}
            >
              Forgot password?
            </button>
          </div>

          <button
            className="form-button auth-anim-field" style={{ "--i": 3 }}
            type="submit" disabled={loading}
          >
            {loading
              ? <span className="auth-btn-loading"><span/><span/><span/></span>
              : "Sign In"}
          </button>
        </form>

        <p className="auth-footer-link auth-anim-field" style={{ "--i": 4 }}>
          Don't have an account? <Link to="/register">Create one</Link>
        </p>
      </section>
    </main>
  );
};

export default Login;
